<template>
  <div ref="editor" @blur="clear()"></div>
</template>

<script>
import { Timestamp } from 'firebase/firestore';
import hljs from 'highlight.js/lib/highlight';
import Quill from 'quill';
import Delta from 'quill-delta';
import ContextLinkBlot from './ContextLinkBlot';
import 'highlight.js/styles/monokai-sublime.css';

//import MagicUrl from 'quill-magic-url';
import debounce from 'lodash/debounce';
//Quill.register('modules/magicUrl', MagicUrl);
Quill.register({
  'formats/context_link': ContextLinkBlot
});

import BetterPaste from './QuillBetterPaste';
Quill.register('modules/betterPaste', BetterPaste);

import { diff3Merge } from 'node-diff3';

hljs.configure({
  // optionally configure hljs
  languages: ['javascript', 'ruby', 'python']
});

export default {
  props: {
    value: {
      type: [Object, String],
      default: () => {
        return {
          html: ''
        };
      }
    },
    blockMode: {},
    updatedOn: {},
    emitText: {
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: ''
    },
    allowEnter: {
      type: Boolean,
      default: false
    },
    catchShiftEnter: {
      type: Boolean,
      default: false
    },
    preventTab: {
      type: Boolean,
      default: false
    },
    stopDefaultTab: {
      type: Boolean,
      default: false
    },
    allowFormats: {
      type: Boolean,
      default: false
    },
    allowMarkdown: {
      type: Boolean,
      default: false
    },
    allowEnterMoveText: {
      type: Boolean,
      default: false
    },
    allowTraversal: {
      type: Boolean,
      default: false
    },
    allowQuoteAttr: {
      type: Boolean,
      default: false
    },
    preventDefaultCtrl: {
      type: Boolean,
      default: false
    },
    hijackUndo: {
      type: Boolean,
      default: false
    },
    hijackCopy: {
      type: Boolean,
      default: false
    },
    hijackCtrl: {
      type: Boolean,
      default: false
    },
    hijackPaste: {
      type: Boolean,
      default: false
    },
    enableLink: {
      default: false
    },
    enableMagicUrl: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      editor: null,
      lastUpdated: null,
      previousVal: null
    };
  },
  computed: {
    htmlVal() {
      return typeof this.value == 'object' ? this.value.html : this.value;
    }
  },
  watch: {
    htmlVal(newVal, oldObjVal) {
      const oldDate = this.lastUpdated;
      const newDate =
        this.value.updatedOn && !!this.value.updatedOn.toDate
          ? this.value.updatedOn.toDate()
          : Timestamp.now().toDate();
      const originalVal = this.emitText ? this.editor.getText() : this.editor.root.innerHTML;

      let curPos;
      let adjust = 0;
      this.lastUpdated = newDate;
      let val = newVal;
      let setType = 'silent';
      //  check if incoming value is different then current
      if (val != originalVal && val !== this.previousVal) {
        //  check if current value is different then previousVal (did we type something unsaved?)
        if (originalVal != this.previousVal && newDate < oldDate) {
          //  Figure out what we have added.
          const mergedData = diff3Merge(val, this.previousVal, originalVal, true);

          console.log('new String', mergedData);
          let mergedArray = [];
          mergedData.forEach(item => {
            console.log('item', item);
            if ('ok' in item) {
              const { ok } = item;
              console.log('ok', ok);
              ok.forEach(letter => mergedArray.push(letter));
            } else if ('conflict' in item) {
              const { b } = item.conflict;
              console.log('conflict b', b);
              b.split('').forEach(letter => mergedArray.push(letter));
            }
          });
          console.log('mergedArray', mergedArray);
          const mergedString = mergedArray.join('');
          console.log('merged string', mergedString);
          val = mergedString;
          setType = 'user';
        } else if (newDate < oldDate) {
          console.log('tried to overwrite newer changes');
          return false;
        }
        const delta = this.editor.clipboard.convert(val);
        if (this.editor.hasFocus()) {
          console.log('has focus!');
          //  figure out if we need to move the cursor
          curPos = this.editor.getSelection().index;
          const originalPreCursorContent = originalVal.slice(0, curPos);
          const originalPostCursorContent = originalVal.slice(curPos, originalVal.length);
          //  check if pre-cursor content changed
          adjust = 0;
          if (
            val.indexOf(originalPreCursorContent) > 0 ||
            val.indexOf(originalPreCursorContent) == -1
          ) {
            //  it's changed, need to figure out by how much
            //  get the
            if (val.indexOf(originalPostCursorContent > 0)) {
              console.log(val.split(originalPostCursorContent));
              const newPreCursorContent = val.split(originalPostCursorContent)
                ? val.split(originalPostCursorContent)[0]
                : '';
              adjust = newPreCursorContent.length - originalPreCursorContent.length;
            }
          }
        }
        console.log('set contents');
        this.editor.setContents(delta, setType);
        this.previousVal = val;
        if (curPos) {
          console.log(curPos, adjust);
          this.editor.setSelection(curPos + adjust);
        }
      }
    },
    disabled(status) {
      this.editor.enable(!status);
    },
    placeholder(v) {
      this.editor.root.setAttribute('data-placeholder', v);
    }
  },
  async mounted() {
    const bindings = {
      alt: {
        key: 18,
        ctrlKey: false,
        handler: () => {
          console.log('alt');
          this.$emit('keyAlt');
          return false;
        }
      },
      ctrlEnter: {
        key: 13,
        shortKey: true,
        handler: (range, context) => {
          const text = this.editor.getText();
          console.log('create container', text, range, context);
          this.$emit('keyCtrlEnter', {
            text
          });
          // this.$emit('keyCtrlEnter');
        }
      },
      shiftEnter: {
        key: 13,
        shiftKey: true,
        ctrlKey: false,
        handler: () => {
          const text = this.editor.getText();
          console.log('create container', text);
          if (this.editor.getFormat()['code-block']) {
            this.$emit('keyEnter', { empty: false, index: 2 });
            return;
          }
          if (!this.catchShiftEnter) {
            return true;
          }
          this.$emit('keyShiftEnter', {
            text
          });
          // this.$emit('keyCtrlEnter');
        }
      },
      ctrlShiftEnter: {
        key: 13,
        shortKey: true,
        shiftKey: true,
        handler: () => {
          const text = this.editor.getText();
          console.log('create flat card', text);
          this.$emit('keyCtrlShiftEnter', {
            text
          });
          // this.$emit('keyCtrlEnter');
        }
      },
      ctrlDown: {
        key: 40,
        shortKey: true,
        handler: () => {
          this.$emit('ctrlDown');
          if (!this.hijackCtrl) {
            return true;
          }
        }
      },
      ctrlUp: {
        key: 38,
        shortKey: true,
        handler: (range, context) => {
          const bounds = this.editor.getBounds(range.index);
          const args = { context, range, bounds };
          this.$emit('ctrlUp', args);
          if (!this.hijackCtrl) {
            return true;
          }
        }
      },
      ctrlAltLeft: {
        key: 37,
        shortKey: true,
        altKey: true,
        handler: () => {
          this.$emit('ctrlAltLeft');
          if (!this.hijackCtrl) {
            return true;
          }
        }
      },
      ctrlAltRight: {
        key: 39,
        shortKey: true,
        altKey: true,
        handler: () => {
          this.$emit('ctrlAltRight');
          if (!this.hijackCtrl) {
            return true;
          }
        }
      },

      ctrlAltDown: {
        key: 40,
        shortKey: true,
        altKey: true,
        handler: () => {
          this.$emit('ctrlAltDown');
          if (!this.hijackCtrl) {
            return true;
          }
        }
      },
      ctrlAltUp: {
        key: 38,
        shortKey: true,
        altKey: true,
        handler: () => {
          this.$emit('ctrlAltUp');
          if (!this.hijackCtrl) {
            return true;
          }
        }
      },
      shiftDown: {
        key: 40,
        ctrlKey: false,
        shiftKey: true,
        handler: range => {
          if (range.index + range.length == this.editor.getLength() - 1) {
            this.$emit('shiftDown');
          } else {
            return true;
          }
        }
      },
      ShiftUp: {
        key: 38,
        ctrlKey: false,
        shiftKey: true,
        handler: range => {
          if (range.index == 0) {
            this.$emit('shiftUp');
          } else {
            return true;
          }
          // return true;
        }
      },
      CtrlA: {
        key: 65,
        shortKey: true,
        shiftKey: false,
        handler: () => {
          this.$emit('ctrlA');
          // return true;
        }
      },
      ctrlShiftDown: {
        key: 40,
        shortKey: true,
        shiftKey: true,
        handler: () => {
          this.$emit('ctrlShiftDown');
          if (!this.hijackCtrl) {
            return true;
          }
        }
      },
      ctrlShiftUp: {
        key: 38,
        shortKey: true,
        shiftKey: true,
        handler: () => {
          this.$emit('ctrlShiftUp');
          if (!this.hijackCtrl) {
            return true;
          }
        }
      },
      alt1: {
        key: 49,
        ctrlKey: null,
        altKey: true,
        handler: () => {
          this.$emit('alt1');
          if (!this.hijackCtrl) {
            return true;
          }
        }
      },
      alt2: {
        key: 50,
        ctrlKey: null,
        altKey: true,
        handler: () => {
          this.$emit('alt2');
          if (!this.hijackCtrl) {
            return true;
          }
        }
      },
      alt3: {
        key: 51,
        ctrlKey: null,
        altKey: true,
        handler: () => {
          this.$emit('alt3');
          if (!this.hijackCtrl) {
            return true;
          }
        }
      },
      // ctrlRight: {
      //   key: 39,
      //   shortKey: true,
      //   handler: () => {
      //     console.log('right');
      //     this.$emit('ctrlRight');
      //     return true;
      //   }
      // },
      enter: {
        key: 13,
        handler: (range, context) => {
          console.log(this.editor.getFormat(), this.editor.getContents(), this.editor.getFormat()['code-block']);
          console.log(context);
          console.log(range);
          //  check if we are are in a bulleted list
          console.log(this.editor);
          if (
            this.allowEnter ||
            this.editor.getFormat()['code-block'] ||
            (this.editor.getFormat().list && this.editor.getLength() > 1)
          ) {
            console.log('code-block!');
            return true;
          }
          let empty = false;
          if (range.index == 0 && this.editor.getLength() == 1) {
            empty = true;
          }
          if (this.allowEnterMoveText && range.index > 0) {
            const enterPoint = range.index;
            const remaining = context.suffix.length;
            console.log(this.editor.getContents(enterPoint));
            console.log(this.editor.getContents(range.index));
            const delta = remaining ? this.editor.getContents(enterPoint) : false;
            this.$emit('keyEnter', { delta, empty, index: range.index });
            if (remaining) {
              //  we hit enter in the middle of a line, delete the rest of the line, we will move it to the next
              const keepDelta = new Delta().retain(enterPoint).delete(remaining);
              const keep = this.editor.getContents(0, enterPoint);
              console.log(keepDelta, keep);
              this.editor.setContents(keep);
            }
          } else {
            this.$emit('keyEnter', { empty, index: range.index });
          }
        }
      },
      'list empty enter': {
        key: 'Enter',
        collapsed: true,
        format: ['list'],
        empty: true,
        handler: (range, context) => {
          if (!this.blockMode) {
            return true;
          }
          const formats = { list: false };
          if (context.format.indent) {
            formats.indent = false;
          }
          this.editor.formatLine(range.index, range.length, formats, Quill.sources.USER);
          console.log('list empty enter');
          if (this.allowEnterMoveText) {
            const enterPoint = range.index;
            const remaining = this.editor.getLength() - enterPoint;
            console.log(this.editor.getContents(enterPoint));
            console.log(this.editor.getContents(range.index));
            const delta = remaining ? this.editor.getContents(enterPoint) : false;
            this.$emit('keyEnter', delta);
            if (remaining) {
              //  we hit enter in the middle of a line, delete the rest of the line, we will move it to the next
              this.editor.updateContents(new Delta().retain(enterPoint).delete(remaining));
            }
          } else {
            let empty = false;
            if (range.index == 0 && this.editor.getLength() == 1) {
              empty = true;
            }
            this.$emit('keyEnter', empty);
          }
        }
      },
      escape: {
        key: 27,
        handler: () => {
          this.editor.setSelection(0);
          this.$emit('keyEsc');
        }
      },
      // shiftTab: {
      //   key: 9,
      //   shiftKey: true,
      //   handler: () => {
      //     return true;
      //     // this.$emit('keyShiftTab');
      //     // return false;
      //   }
      // },
      ctrlRight: {
        key: 39,
        shortKey: true,
        handler: () => {
          this.$emit('ctrlRight');
          if (!this.preventDefaultCtrl) {
            return true;
          }
        }
      },
      ctrlLeft: {
        key: 37,
        shortKey: true,
        handler: () => {
          this.$emit('ctrlLeft');
          if (!this.preventDefaultCtrl) {
            return true;
          }
        }
      },
      arrowLeft: {
        key: 37,
        ctrlKey: false,
        offset: 0,
        handler: () => {
          this.$emit('keyLeftFirstChar');
        }
      },
      arrowRight: {
        key: 39,
        ctrlKey: false,
        suffix: /^$/,
        handler: range => {
          if (range.index + 1 == this.editor.getLength()) {
            this.$emit('keyRightLastChar');
          } else {
            return true;
          }
        }
      },
      backspace: {
        key: 8,
        handler: range => {
          console.log(this.editor.getLength());
          if (range.index == 0 && this.editor.getLength() == 1) {
            this.$emit('keyBackspaceEmpty');
            return false;
          } else if (range.index == 0 && range.length == 0) {
            const delta = this.editor.getLength() > 1 ? this.editor.getContents() : false;
            this.$emit('keyBackspaceFirstChar', delta);
            return false;
          }
          return true;
        }
      },
      deleteLastChar: {
        key: 46,
        suffix: /^$/,
        handler: range => {
          if (range.length == 0) {
            const delta = this.editor.getLength() > 1 ? this.editor.getContents() : false;
            this.$emit('keyDeleteLastChar', delta);
            return false;
          } else {
            return true;
          }
        }
      },
      slash: {
        key: 191,
        shortKey: true,
        // offset: 0,
        handler: () => {
          this.$emit('slash');
        }
      },
      //  Hijack Undo
      undo: {
        key: 90,
        shortKey: true,
        shiftKey: false,
        handler: () => {
          console.log('undo');
          if (this.hijackUndo) {
            this.$emit('undo');
            return false;
          } else {
            return true;
          }
        }
      },
      redo: {
        key: 90,
        shiftKey: true,
        shortKey: true,
        handler: () => {
          if (this.hijackUndo) {
            this.$emit('redo');
            return false;
          } else {
            return true;
          }
        }
      },
      //  Hijack copy and paste
      copy: {
        key: 67,
        shortKey: true,
        handler: () => {
          console.log('copy');
          if (this.hijackCopy) {
            console.log('copy');
            this.$emit('copy');
            return false;
          } else {
            console.log('false copy');
            return true;
          }
        }
      },
      //  Hijack copy and paste
      paste: {
        key: 86,
        shortKey: true,
        handler: () => {
          if (this.hijackPaste) {
            console.log('paste');
            this.$emit('paste');
            return false;
          } else {
            console.log('false paste');
            return true;
          }
        }
      },
      //  Handle markdown shortcodes
      // # to H1
      markdownHeader: {
        key: 32, // space
        offset: 2,
        shiftKey: null,
        prefix: /^##$/,
        handler: range => {
          this.$emit('mdPound');
          this.editor.deleteText(range.index - 2, 2);
        }
      },
      // 'header enter': {
      //   // overwrite quills default behavior here
      //   key: 'Enter',
      //   handler: (range, context) => {
      //     console.log('header enter');
      //     this.$emit('keyEnter', false);
      //   }
      // },
      markdownCode: {
        key: 32, // space
        offset: 3,
        shiftKey: null,
        prefix: /^```$/,
        handler: (range, context) => {
          if (!this.allowMarkdown) {
            return true;
          }
          // this.$emit('setType', 'thought');
          this.editor.format('code-block', true);
        }
      },
      mdDash: {
        key: 32, // space
        offset: 2,
        shiftKey: null,
        prefix: /^--$/,
        handler: (range, context) => {
          if (!this.allowMarkdown) {
            return true;
          }
          // const text = this.editor.getText();
          this.editor.deleteText(range.index - 2, 2);
          this.$emit('mdDash');
        }
      },
      mdMoney: {
        key: 32, // space
        offset: 2,
        shiftKey: null,
        prefix: /^\$\$$/,
        handler: (range, context) => {
          if (!this.allowMarkdown) {
            return true;
          }
          this.editor.deleteText(range.index - 2, 2);
          this.$emit('mdMoney');
        }
      },
      mdArrow: {
        key: 32, // space
        offset: null,
        shiftKey: null,
        prefix: /^>>$/,
        handler: (range, context) => {
          if (!this.allowMarkdown) {
            return true;
          }
          console.log('arrow');
          // const text = this.editor.getText();
          this.editor.deleteText(range.index - 2, 2);
          this.$emit('mdArrow');
        }
      },
      mdM: {
        key: 32, // spac
        offset: null,
        shiftKey: null,
        prefix: /^mm$/,
        handler: (range, context) => {
          if (!this.allowMarkdown) {
            return true;
          }
          console.log('m');
          // const text = this.editor.getText();
          this.editor.deleteText(range.index - 2, 2);
          this.$emit('mdM');
        }
      },
      mdPeriod: {
        key: 32, // space
        offset: null,
        shiftKey: null,
        prefix: /^\.\.$/,
        handler: (range, context) => {
          if (!this.allowMarkdown) {
            return true;
          }
          console.log('period');
          // const text = this.editor.getText();
          this.editor.deleteText(range.index - 2, 2);
          this.$emit('mdPeriod');
        }
      },
      mdBracket: {
        key: 32, // space
        offset: 2,
        shiftKey: null,
        prefix: /^\[\[$/,
        handler: (range, context) => {
          if (!this.allowMarkdown) {
            return true;
          }
          // const text = this.editor.getText();
          this.editor.deleteText(range.index - 2, 2);
          this.$emit('mdBracket');
        }
      },
      mdSquigBrack: {
        key: 32, // space
        offset: 2,
        shiftKey: null,
        prefix: /^\{\{/,
        handler: (range, context) => {
          if (!this.allowMarkdown) {
            return true;
          }
          // const text = this.editor.getText();
          this.editor.deleteText(range.index - 2, 2);
          this.$emit('mdSquigBrack');
        }
      },
      mdPercent: {
        key: 32, // space
        offset: 2,
        shiftKey: null,
        prefix: /^%%/,
        handler: (range, context) => {
          if (!this.allowMarkdown) {
            return true;
          }
          // const text = this.editor.getText();
          this.editor.deleteText(range.index - 2, 2);
          this.$emit('mdPercent');
        }
      },
      mdColon: {
        key: 32, // space
        offset: null,
        shiftKey: null,
        prefix: /^::$/,
        handler: (range, context) => {
          if (!this.allowMarkdown) {
            return true;
          }
          console.log('colon');
          // const text = this.editor.getText();
          this.editor.deleteText(range.index - 2, 2);
          this.$emit('mdColon');
        }
      },
      mdCaret: {
        key: 32, // space
        // offset: 2,
        shiftKey: null,
        prefix: /^\^\^$/,
        handler: (range, context) => {
          if (!this.allowMarkdown) {
            return true;
          }
          // const text = this.editor.getText();
          this.editor.deleteText(range.index - 2, 2);
          this.$emit('mdCaret');
        }
      },
      mdSlash: {
        key: 32, // space
        offset: 2,
        shiftKey: null,
        prefix: /^\/\/$/,
        handler: (range, context) => {
          if (!this.allowMarkdown) {
            return true;
          }
          const text = this.editor.getText();
          this.editor.deleteText(range.index - 2, 2);
          this.$emit('mdSlash');
        }
      },
      mdQuestion: {
        key: 32, // space
        offset: 2,
        shiftKey: null,
        prefix: /^\?\?$/,
        handler: (range, context) => {
          if (!this.allowMarkdown) {
            return true;
          }
          console.log('question');
          // const text = this.editor.getText();
          console.log(range.index);
          this.editor.deleteText(range.index - 2, 2);
          this.$emit('mdQuestion');
        }
      },
      mdAstrisk: {
        key: 32, // space
        offset: 2,
        prefix: /^\*\*$/,
        shiftKey: null,
        handler: range => {
          if (!this.allowMarkdown) {
            return true;
          }
          console.log('question');
          const text = this.editor.getText();
          this.editor.deleteText(range.index - 2, 2);
          this.$emit('mdAstrisk', text);
        }
      },
      mdExclaimation: {
        key: 32, // space
        offset: 2,
        prefix: /^!!$/,
        shiftKey: null,
        handler: range => {
          if (!this.allowMarkdown) {
            return true;
          }
          console.log('question');
          const text = this.editor.getText();
          this.editor.deleteText(range.index - 2, 2);
          this.$emit('mdExclaimation', text);
        }
      },
      mdTask: {
        key: 32, // space
        offset: 2,
        shiftKey: null,
        prefix: /^@@$/,
        handler: (range, context) => {
          if (!this.allowMarkdown) {
            return true;
          }
          this.editor.deleteText(range.index - 2, 2);
          this.$emit('mdTask');
        }
      },
      mdSemiColon: {
        key: 32, // space
        offset: 2,
        shiftKey: null,
        prefix: /^;;$/,
        handler: (range, context) => {
          if (!this.allowMarkdown) {
            return true;
          }
          this.editor.deleteText(range.index - 2, 2);
          this.$emit('mdSemiColon');
        }
      },
      mdQuote: {
        key: 32, // space
        offset: 2,
        shiftKey: null,
        prefix: /^""$/,
        handler: (range, context) => {
          if (!this.allowMarkdown) {
            return true;
          }
          this.editor.deleteText(range.index - 2, 2);
          this.$emit('mdQuote');
        }
      },
      embedContext: {
        key: 187, // plus
        shiftKey: true,
        //prefix: /^\+$/,
        offset: 0,
        handler: (range, context) => {
          console.log('quote');
          // if (!this.allowQuoteAttr) {
          //   return true;
          // }
          const selection = document.getSelection();
          console.log(selection);
          const pos = selection.getRangeAt(0).getClientRects();
          console.log('prompt add context', pos);
          this.$emit('mdPlus', {
            pos,
            cancel: function() {
              console.log('cancel');
              return true;
            }
          });
          return false;
        }
      }
    };
    // bind tab to default browser behavior if prevent is enabled
    if (this.preventTab) {
      bindings['tab'] = {
        key: 9,
        handler: range => {
          console.log('tab');
          const bounds = this.editor.getBounds(range.index);
          this.$emit('keyTab', { bounds, empty: this.editor.getLength() == 1 });
          if (!this.stopDefaultTab) {
            console.log('let tab happen');
            return true;
          }
          // if (this.preventTab) {
          //   console.log('prevent tab');
          //   this.$emit('keyTab');
          // } else {
          //   return true;
          // }
        }
      };
      bindings['removeTab'] = {
        key: 9,
        shiftKey: true,
        handler: range => {
          console.log('shift tab');
          const bounds = this.editor.getBounds(range.index);
          this.$emit('keyShiftTab', { bounds });
          if (!this.stopDefaultTab) {
            return true;
          }
          // if (this.preventTab) {
          //   console.log('prevent tab');
          //   this.$emit('keyTab');
          // } else {
          //   return true;
          // }
        }
      };
    }

    if (this.allowTraversal) {
      bindings['arrowDown'] = {
        key: 40,
        handler: (range, context) => {
          const bounds = this.editor.getBounds(range.index);
          const isLastLine = this.$refs.editor.offsetHeight - bounds.bottom < 10;
          if (isLastLine) {
            const args = { context, range, bounds };
            this.$emit('keyDownLastLine', args);
            return false;
          } else {
            return true;
          }
        }
      };
      bindings['arrowUp'] = {
        key: 38,
        handler: (range, context) => {
          const bounds = this.editor.getBounds(range.index);
          // console.log(bounds);
          const isFirstLine = bounds.top < 10;
          if (isFirstLine) {
            const args = { context, range, bounds };
            this.$emit('keyUpFirstLine', args);
            return false;
          } else {
            return true;
          }
        }
      };
    }

    if (this.blockMode || this.preventEnter) {
      bindings['header enter'] = {
        // overwrite quills default behavior here
        key: 'Enter',
        handler: (range, context) => {
          console.log('header enter');
          this.$emit('keyEnter', false);
        }
      };
    }

    this.editor = new Quill(this.$refs.editor, {
      formats: this.allowFormats ? this.formats : [],
      placeholder: this.placeholder,
      modules: {
        toolbar: this.enableLink,
        history: false,
        syntax: {
          highlight: text => hljs.highlightAuto(text).value
        },
        betterPaste: {
          pasteClean: false,
          pasteLink: this.enableLink,
          typeLink: this.enableLink
        },
        //magicUrl: this.enableMagicUrl,
        keyboard: { bindings }
      },
      theme: this.enableLink ? 'snow' : false,
      readOnly: this.disabled ? this.disabled : false
    });

    const startingDelta = this.editor.clipboard.convert(this.htmlVal);
    this.editor.setContents(startingDelta, 'silent');
    this.lastUpdated =
      this.value.updatedOn && this.value.updatedOn.toDate
        ? this.value.updatedOn.toDate()
        : new Date();
    this.previousVal = this.htmlVal;
    this.editor.on('text-change', (delta, oldDelta, source) => {
      if (source != 'silent') {
        //  TODO: maybe if source is user and debounce on we debounce this?
        this.update();
      }
    });

    this.editor.on('selection-change', (range, oldRange, source) => {
      if (range === null && oldRange !== null) {
        console.log('blur selection change', source);
        this.blur();
        //  hide hlink pop over
        if (this.enableLink) {
          setTimeout(() => {
            const tip = this.$el.querySelector('.ql-tooltip');
            if (tip) {
              console.log(tip.classList);
              if (!tip.classList.contains('ql-editing')) {
                tip.classList.add('ql-hidden');
              }
            }
          }, 100);
        }
      } else {
        if (range.length == 0) {
          console.log('User cursor is on', range.index);
        }
        if (!oldRange && !this.disabled) {
          console.log('focus');
          this.$emit('focus');
        }
      }
    });
    this.updatePrevious = debounce(this.updatePreviousMethod.bind(this), 1000);
    this.$emit('editorReady');
  },
  beforeDestroy() {
    this.editor = null;
    delete this.editor;
  },
  methods: {
    update() {
      const text =
        this.editor && this.editor.getText()
          ? this.emitText
            ? this.editor.getText()
            : this.editor.root.innerHTML
          : '';
      this.$emit('input', text);
      this.updatePrevious(text);
    },
    updatePreviousMethod(val) {
      //  Update the previous value after a wait of 1s, simulates updates to the db
      console.log('I only get fired once every two seconds, max!');
      console.log('this', this);
      this.previousVal = val;
    },
    setContents(delta) {
      this.editor.setContents(delta);
    },
    getContents() {
      return this.editor.getContents();
    },
    updateContents(delta) {
      //const currentDelta = this.editor.getContents();
      const oldDelta = new Delta().retain(this.editor.getLength() - 1).delete(1);
      const newDelta = delta ? oldDelta.concat(delta) : oldDelta;
      this.editor.updateContents(newDelta);
    },
    focus(selectAll = false) {
      //this.editor.focus();
      const length = this.editor.getLength();
      if (length > 1 && selectAll) {
        // highlight the text
        this.editor.setSelection(0, length);
      } else {
        // just focus the input
        this.editor.focus();
      }
    },
    setCaret({ prevLeft = 0, direction = 'down' }) {
      const newIndex = this.findIndex(prevLeft, direction);
      this.editor.setSelection(newIndex);
    },
    blur() {
      this.$emit('blur');
    },
    formatLine(format) {
      //  format is p, 1, or 2
      console.log(format);
      if (format === 'p') {
        console.log('remove format');
        this.editor.format('header', false, 'user');
      } else {
        console.log('formatLine');
        this.editor.format('header', format, 'user');
      }
    },
    getFormat() {
      return this.editor.getFormat();
    },
    findIndex(left, direction = 'down') {
      let dist;
      let oldDist;
      let length = this.editor.getLength();
      let i = 0;
      if (direction == 'down') {
        while (i < length) {
          let bounds = this.editor.getBounds(i);
          dist = Math.abs(bounds.left - left);
          if (!isNaN(oldDist) && oldDist < dist) {
            i--;
            break;
          } else {
            oldDist = dist;
            i++;
          }
        }
      } else {
        i = length - 1;
        while (i >= 0) {
          let bounds = this.editor.getBounds(i);
          dist = Math.abs(bounds.left - left);
          if (!isNaN(oldDist) && oldDist < dist) {
            i++;
            break;
          } else {
            oldDist = dist;
            i--;
          }
        }
      }

      return i;
    }
  }
};
</script>

<style>
.ql-editor.ql-blank::before {
  color: rgba(0, 0, 0, 0.4);
  content: attr(data-placeholder);
  font-style: none;
  pointer-events: none;
  position: absolute;
}

.ql-disabled .ql-editor {
  pointer-events: none;
  user-select: none;
}

[contenteditable='true']:empty::before {
  content: attr(placeholder);
  opacity: 0.2;
}

.ql-editor {
  min-height: auto;
  font-size: inherit;
  border: none;
  outline: none;
  padding: 0px;
  white-space: pre-wrap;
}

.ql-container.ql-snow {
  border: 0px;
  background-color: transparent;
}

.ql-clipboard {
  left: -100000px;
  height: 1px;
  overflow-y: hidden;
  position: absolute;
  top: 50%;
}

.editing .text-line {
  cursor: text;
}

.ql-editor p,
.ql-editor ol,
.ql-editor ul,
.ql-editor pre,
.ql-editor blockquote,
.ql-editor h1,
.ql-editor h2,
.ql-editor h3,
.ql-editor h4,
.ql-editor h5,
.ql-editor h6 {
  margin: 0;
  padding: 0;
  counter-reset: list-1 list-2 list-3 list-4 list-5 list-6 list-7 list-8 list-9;
}
.ql-editor ol,
.ql-editor ul {
  padding-left: 0.2em;
}
.ql-editor ol > li,
.ql-editor ul > li {
  list-style-type: none;
}
.ql-editor ul > li::before {
  content: '\2022';
}
.ql-editor ul[data-checked='true'],
.ql-editor ul[data-checked='false'] {
  pointer-events: none;
}
.ql-editor ul[data-checked='true'] > li *,
.ql-editor ul[data-checked='false'] > li * {
  pointer-events: all;
}
.ql-editor ul[data-checked='true'] > li::before,
.ql-editor ul[data-checked='false'] > li::before {
  color: #777;
  cursor: pointer;
  pointer-events: all;
}
.ql-editor ul[data-checked='true'] > li::before {
  content: '\2611';
}
.ql-editor ul[data-checked='false'] > li::before {
  content: '\2610';
}
.ql-editor li::before {
  display: inline-block;
  white-space: nowrap;
  width: 1.2em;
}
.ql-editor li:not(.ql-direction-rtl)::before {
  margin-left: -1.5em;
  margin-right: 0.3em;
  text-align: right;
}
.ql-editor li.ql-direction-rtl::before {
  margin-left: 0.3em;
  margin-right: -1.5em;
}
.ql-editor ol li:not(.ql-direction-rtl),
.ql-editor ul li:not(.ql-direction-rtl) {
  padding-left: 1.5em;
}
.ql-editor ol li.ql-direction-rtl,
.ql-editor ul li.ql-direction-rtl {
  padding-right: 1.5em;
}
.ql-editor ol li {
  counter-reset: list-1 list-2 list-3 list-4 list-5 list-6 list-7 list-8 list-9;
  counter-increment: list-0;
}
.ql-editor ol li:before {
  content: counter(list-0, decimal) '. ';
}
.ql-editor ol li.ql-indent-1 {
  counter-increment: list-1;
}
.ql-editor ol li.ql-indent-1:before {
  content: counter(list-1, lower-alpha) '. ';
}
.ql-editor ol li.ql-indent-1 {
  counter-reset: list-2 list-3 list-4 list-5 list-6 list-7 list-8 list-9;
}
.ql-editor ol li.ql-indent-2 {
  counter-increment: list-2;
}
.ql-editor ol li.ql-indent-2:before {
  content: counter(list-2, lower-roman) '. ';
}
.ql-editor ol li.ql-indent-2 {
  counter-reset: list-3 list-4 list-5 list-6 list-7 list-8 list-9;
}
.ql-editor ol li.ql-indent-3 {
  counter-increment: list-3;
}
.ql-editor ol li.ql-indent-3:before {
  content: counter(list-3, decimal) '. ';
}
.ql-editor ol li.ql-indent-3 {
  counter-reset: list-4 list-5 list-6 list-7 list-8 list-9;
}
.ql-editor ol li.ql-indent-4 {
  counter-increment: list-4;
}
.ql-editor ol li.ql-indent-4:before {
  content: counter(list-4, lower-alpha) '. ';
}
.ql-editor ol li.ql-indent-4 {
  counter-reset: list-5 list-6 list-7 list-8 list-9;
}
.ql-editor ol li.ql-indent-5 {
  counter-increment: list-5;
}
.ql-editor ol li.ql-indent-5:before {
  content: counter(list-5, lower-roman) '. ';
}
.ql-editor ol li.ql-indent-5 {
  counter-reset: list-6 list-7 list-8 list-9;
}
.ql-editor ol li.ql-indent-6 {
  counter-increment: list-6;
}
.ql-editor ol li.ql-indent-6:before {
  content: counter(list-6, decimal) '. ';
}
.ql-editor ol li.ql-indent-6 {
  counter-reset: list-7 list-8 list-9;
}
.ql-editor ol li.ql-indent-7 {
  counter-increment: list-7;
}
.ql-editor ol li.ql-indent-7:before {
  content: counter(list-7, lower-alpha) '. ';
}
.ql-editor ol li.ql-indent-7 {
  counter-reset: list-8 list-9;
}
.ql-editor ol li.ql-indent-8 {
  counter-increment: list-8;
}
.ql-editor ol li.ql-indent-8:before {
  content: counter(list-8, lower-roman) '. ';
}
.ql-editor ol li.ql-indent-8 {
  counter-reset: list-9;
}
.ql-editor ol li.ql-indent-9 {
  counter-increment: list-9;
}
.ql-editor ol li.ql-indent-9:before {
  content: counter(list-9, decimal) '. ';
}
.ql-editor .ql-indent-1:not(.ql-direction-rtl) {
  padding-left: 3em;
}
.ql-editor li.ql-indent-1:not(.ql-direction-rtl) {
  padding-left: 3em;
}
.ql-editor .ql-indent-1.ql-direction-rtl.ql-align-right {
  padding-right: 3em;
}
.ql-editor li.ql-indent-1.ql-direction-rtl.ql-align-right {
  padding-right: 3em;
}
.ql-editor .ql-indent-2:not(.ql-direction-rtl) {
  padding-left: 6em;
}
.ql-editor li.ql-indent-2:not(.ql-direction-rtl) {
  padding-left: 4em;
}
.ql-editor .ql-indent-2.ql-direction-rtl.ql-align-right {
  padding-right: 6em;
}
.ql-editor li.ql-indent-2.ql-direction-rtl.ql-align-right {
  padding-right: 4em;
}
.ql-editor .ql-indent-3:not(.ql-direction-rtl) {
  padding-left: 9em;
}
.ql-editor li.ql-indent-3:not(.ql-direction-rtl) {
  padding-left: 10.5em;
}
.ql-editor .ql-indent-3.ql-direction-rtl.ql-align-right {
  padding-right: 9em;
}
.ql-editor li.ql-indent-3.ql-direction-rtl.ql-align-right {
  padding-right: 10.5em;
}
.ql-editor .ql-indent-4:not(.ql-direction-rtl) {
  padding-left: 12em;
}
.ql-editor li.ql-indent-4:not(.ql-direction-rtl) {
  padding-left: 13.5em;
}
.ql-editor .ql-indent-4.ql-direction-rtl.ql-align-right {
  padding-right: 12em;
}
.ql-editor li.ql-indent-4.ql-direction-rtl.ql-align-right {
  padding-right: 13.5em;
}
.ql-editor .ql-indent-5:not(.ql-direction-rtl) {
  padding-left: 15em;
}
.ql-editor li.ql-indent-5:not(.ql-direction-rtl) {
  padding-left: 16.5em;
}
.ql-editor .ql-indent-5.ql-direction-rtl.ql-align-right {
  padding-right: 15em;
}
.ql-editor li.ql-indent-5.ql-direction-rtl.ql-align-right {
  padding-right: 16.5em;
}
.ql-editor .ql-indent-6:not(.ql-direction-rtl) {
  padding-left: 18em;
}
.ql-editor li.ql-indent-6:not(.ql-direction-rtl) {
  padding-left: 19.5em;
}
.ql-editor .ql-indent-6.ql-direction-rtl.ql-align-right {
  padding-right: 18em;
}
.ql-editor li.ql-indent-6.ql-direction-rtl.ql-align-right {
  padding-right: 19.5em;
}
.ql-editor .ql-indent-7:not(.ql-direction-rtl) {
  padding-left: 21em;
}
.ql-editor li.ql-indent-7:not(.ql-direction-rtl) {
  padding-left: 22.5em;
}
.ql-editor .ql-indent-7.ql-direction-rtl.ql-align-right {
  padding-right: 21em;
}
.ql-editor li.ql-indent-7.ql-direction-rtl.ql-align-right {
  padding-right: 22.5em;
}
.ql-editor .ql-indent-8:not(.ql-direction-rtl) {
  padding-left: 24em;
}
.ql-editor li.ql-indent-8:not(.ql-direction-rtl) {
  padding-left: 25.5em;
}
.ql-editor .ql-indent-8.ql-direction-rtl.ql-align-right {
  padding-right: 24em;
}
.ql-editor li.ql-indent-8.ql-direction-rtl.ql-align-right {
  padding-right: 25.5em;
}
.ql-editor .ql-indent-9:not(.ql-direction-rtl) {
  padding-left: 27em;
}
.ql-editor li.ql-indent-9:not(.ql-direction-rtl) {
  padding-left: 28.5em;
}
.ql-editor .ql-indent-9.ql-direction-rtl.ql-align-right {
  padding-right: 27em;
}
.ql-editor li.ql-indent-9.ql-direction-rtl.ql-align-right {
  padding-right: 28.5em;
}

.ql-tooltip {
  background-color: #fff;
  border: 1px solid #ccc;
  -webkit-box-shadow: 0px 0px 5px #ddd;
  box-shadow: 0px 0px 5px #ddd;
  color: #444;
  padding: 5px 12px;
  z-index: 1;
  white-space: nowrap;
}

.ql-toolbar {
  display: none;
}

/* Snow theme stuff for link tool tip */
.ql-snow.ql-toolbar:after,
.ql-snow .ql-toolbar:after {
  clear: both;
  content: '';
  display: table;
}
.ql-snow.ql-toolbar button,
.ql-snow .ql-toolbar button {
  background: none;
  border: none;
  cursor: pointer;
  display: inline-block;
  float: left;
  height: 24px;
  padding: 3px 5px;
  width: 28px;
}
.ql-snow.ql-toolbar button svg,
.ql-snow .ql-toolbar button svg {
  float: left;
  height: 100%;
}
.ql-snow.ql-toolbar button:active:hover,
.ql-snow .ql-toolbar button:active:hover {
  outline: none;
}
.ql-snow.ql-toolbar input.ql-image[type='file'],
.ql-snow .ql-toolbar input.ql-image[type='file'] {
  display: none;
}
.ql-snow.ql-toolbar button:hover,
.ql-snow .ql-toolbar button:hover,
.ql-snow.ql-toolbar button:focus,
.ql-snow .ql-toolbar button:focus,
.ql-snow.ql-toolbar button.ql-active,
.ql-snow .ql-toolbar button.ql-active,
.ql-snow.ql-toolbar .ql-picker-label:hover,
.ql-snow .ql-toolbar .ql-picker-label:hover,
.ql-snow.ql-toolbar .ql-picker-label.ql-active,
.ql-snow .ql-toolbar .ql-picker-label.ql-active,
.ql-snow.ql-toolbar .ql-picker-item:hover,
.ql-snow .ql-toolbar .ql-picker-item:hover,
.ql-snow.ql-toolbar .ql-picker-item.ql-selected,
.ql-snow .ql-toolbar .ql-picker-item.ql-selected {
  color: #06c;
}
.ql-snow.ql-toolbar button:hover .ql-fill,
.ql-snow .ql-toolbar button:hover .ql-fill,
.ql-snow.ql-toolbar button:focus .ql-fill,
.ql-snow .ql-toolbar button:focus .ql-fill,
.ql-snow.ql-toolbar button.ql-active .ql-fill,
.ql-snow .ql-toolbar button.ql-active .ql-fill,
.ql-snow.ql-toolbar .ql-picker-label:hover .ql-fill,
.ql-snow .ql-toolbar .ql-picker-label:hover .ql-fill,
.ql-snow.ql-toolbar .ql-picker-label.ql-active .ql-fill,
.ql-snow .ql-toolbar .ql-picker-label.ql-active .ql-fill,
.ql-snow.ql-toolbar .ql-picker-item:hover .ql-fill,
.ql-snow .ql-toolbar .ql-picker-item:hover .ql-fill,
.ql-snow.ql-toolbar .ql-picker-item.ql-selected .ql-fill,
.ql-snow .ql-toolbar .ql-picker-item.ql-selected .ql-fill,
.ql-snow.ql-toolbar button:hover .ql-stroke.ql-fill,
.ql-snow .ql-toolbar button:hover .ql-stroke.ql-fill,
.ql-snow.ql-toolbar button:focus .ql-stroke.ql-fill,
.ql-snow .ql-toolbar button:focus .ql-stroke.ql-fill,
.ql-snow.ql-toolbar button.ql-active .ql-stroke.ql-fill,
.ql-snow .ql-toolbar button.ql-active .ql-stroke.ql-fill,
.ql-snow.ql-toolbar .ql-picker-label:hover .ql-stroke.ql-fill,
.ql-snow .ql-toolbar .ql-picker-label:hover .ql-stroke.ql-fill,
.ql-snow.ql-toolbar .ql-picker-label.ql-active .ql-stroke.ql-fill,
.ql-snow .ql-toolbar .ql-picker-label.ql-active .ql-stroke.ql-fill,
.ql-snow.ql-toolbar .ql-picker-item:hover .ql-stroke.ql-fill,
.ql-snow .ql-toolbar .ql-picker-item:hover .ql-stroke.ql-fill,
.ql-snow.ql-toolbar .ql-picker-item.ql-selected .ql-stroke.ql-fill,
.ql-snow .ql-toolbar .ql-picker-item.ql-selected .ql-stroke.ql-fill {
  fill: #06c;
}
.ql-snow.ql-toolbar button:hover .ql-stroke,
.ql-snow .ql-toolbar button:hover .ql-stroke,
.ql-snow.ql-toolbar button:focus .ql-stroke,
.ql-snow .ql-toolbar button:focus .ql-stroke,
.ql-snow.ql-toolbar button.ql-active .ql-stroke,
.ql-snow .ql-toolbar button.ql-active .ql-stroke,
.ql-snow.ql-toolbar .ql-picker-label:hover .ql-stroke,
.ql-snow .ql-toolbar .ql-picker-label:hover .ql-stroke,
.ql-snow.ql-toolbar .ql-picker-label.ql-active .ql-stroke,
.ql-snow .ql-toolbar .ql-picker-label.ql-active .ql-stroke,
.ql-snow.ql-toolbar .ql-picker-item:hover .ql-stroke,
.ql-snow .ql-toolbar .ql-picker-item:hover .ql-stroke,
.ql-snow.ql-toolbar .ql-picker-item.ql-selected .ql-stroke,
.ql-snow .ql-toolbar .ql-picker-item.ql-selected .ql-stroke,
.ql-snow.ql-toolbar button:hover .ql-stroke-miter,
.ql-snow .ql-toolbar button:hover .ql-stroke-miter,
.ql-snow.ql-toolbar button:focus .ql-stroke-miter,
.ql-snow .ql-toolbar button:focus .ql-stroke-miter,
.ql-snow.ql-toolbar button.ql-active .ql-stroke-miter,
.ql-snow .ql-toolbar button.ql-active .ql-stroke-miter,
.ql-snow.ql-toolbar .ql-picker-label:hover .ql-stroke-miter,
.ql-snow .ql-toolbar .ql-picker-label:hover .ql-stroke-miter,
.ql-snow.ql-toolbar .ql-picker-label.ql-active .ql-stroke-miter,
.ql-snow .ql-toolbar .ql-picker-label.ql-active .ql-stroke-miter,
.ql-snow.ql-toolbar .ql-picker-item:hover .ql-stroke-miter,
.ql-snow .ql-toolbar .ql-picker-item:hover .ql-stroke-miter,
.ql-snow.ql-toolbar .ql-picker-item.ql-selected .ql-stroke-miter,
.ql-snow .ql-toolbar .ql-picker-item.ql-selected .ql-stroke-miter {
  stroke: #06c;
}
@media (pointer: coarse) {
  .ql-snow.ql-toolbar button:hover:not(.ql-active),
  .ql-snow .ql-toolbar button:hover:not(.ql-active) {
    color: #444;
  }
  .ql-snow.ql-toolbar button:hover:not(.ql-active) .ql-fill,
  .ql-snow .ql-toolbar button:hover:not(.ql-active) .ql-fill,
  .ql-snow.ql-toolbar button:hover:not(.ql-active) .ql-stroke.ql-fill,
  .ql-snow .ql-toolbar button:hover:not(.ql-active) .ql-stroke.ql-fill {
    fill: #444;
  }
  .ql-snow.ql-toolbar button:hover:not(.ql-active) .ql-stroke,
  .ql-snow .ql-toolbar button:hover:not(.ql-active) .ql-stroke,
  .ql-snow.ql-toolbar button:hover:not(.ql-active) .ql-stroke-miter,
  .ql-snow .ql-toolbar button:hover:not(.ql-active) .ql-stroke-miter {
    stroke: #444;
  }
}
.ql-snow {
  box-sizing: border-box;
}
.ql-snow * {
  box-sizing: border-box;
}
.ql-snow .ql-hidden {
  display: none;
}
.ql-snow .ql-out-bottom,
.ql-snow .ql-out-top {
  visibility: hidden;
}
.ql-snow .ql-tooltip {
  position: absolute;
  transform: translateY(10px);
}
.ql-snow .ql-tooltip a {
  cursor: pointer;
  text-decoration: none;
}
.ql-snow .ql-tooltip.ql-flip {
  transform: translateY(-10px);
}
.ql-snow .ql-formats {
  display: inline-block;
  vertical-align: middle;
}
.ql-snow .ql-formats:after {
  clear: both;
  content: '';
  display: table;
}
.ql-snow .ql-stroke {
  fill: none;
  stroke: #444;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-width: 2;
}
.ql-snow .ql-stroke-miter {
  fill: none;
  stroke: #444;
  stroke-miterlimit: 10;
  stroke-width: 2;
}
.ql-snow .ql-fill,
.ql-snow .ql-stroke.ql-fill {
  fill: #444;
}
.ql-snow .ql-empty {
  fill: none;
}
.ql-snow .ql-even {
  fill-rule: evenodd;
}
.ql-snow .ql-thin,
.ql-snow .ql-stroke.ql-thin {
  stroke-width: 1;
}
.ql-snow .ql-transparent {
  opacity: 0.4;
}
.ql-snow .ql-direction svg:last-child {
  display: none;
}
.ql-snow .ql-direction.ql-active svg:last-child {
  display: inline;
}
.ql-snow .ql-direction.ql-active svg:first-child {
  display: none;
}

.ql-snow .ql-editor a {
  text-decoration: underline;
}
.ql-snow .ql-editor blockquote {
  border-left: 4px solid #ccc;
  margin-bottom: 5px;
  margin-top: 5px;
  padding-left: 16px;
}
.ql-snow .ql-editor code,
.ql-snow .ql-editor pre {
  background-color: #f0f0f0;
  border-radius: 3px;
}
.ql-snow .ql-editor pre {
  white-space: pre-wrap;
  margin-bottom: 5px;
  margin-top: 5px;
  padding: 5px 10px;
}
.ql-snow .ql-editor code {
  font-size: 85%;
  padding: 2px 4px;
}
.ql-snow .ql-editor pre.ql-syntax {
  background-color: #23241f;
  color: #f8f8f2;
  overflow: visible;
}
.ql-editor img {
  max-width: 100%;
}
.ql-snow .ql-picker {
  color: #444;
  display: inline-block;
  float: left;
  font-size: 14px;
  font-weight: 500;
  height: 24px;
  position: relative;
  vertical-align: middle;
}
.ql-snow .ql-picker-label {
  cursor: pointer;
  display: inline-block;
  height: 100%;
  padding-left: 8px;
  padding-right: 2px;
  position: relative;
  width: 100%;
}
.ql-snow .ql-picker-label::before {
  display: inline-block;
  line-height: 22px;
}
.ql-snow .ql-picker-options {
  background-color: #fff;
  display: none;
  min-width: 100%;
  padding: 4px 8px;
  position: absolute;
  white-space: nowrap;
}
.ql-snow .ql-picker-options .ql-picker-item {
  cursor: pointer;
  display: block;
  padding-bottom: 5px;
  padding-top: 5px;
}
.ql-snow .ql-picker.ql-expanded .ql-picker-label {
  color: #ccc;
  z-index: 2;
}
.ql-snow .ql-picker.ql-expanded .ql-picker-label .ql-fill {
  fill: #ccc;
}
.ql-snow .ql-picker.ql-expanded .ql-picker-label .ql-stroke {
  stroke: #ccc;
}
.ql-snow .ql-picker.ql-expanded .ql-picker-options {
  display: block;
  margin-top: -1px;
  top: 100%;
  z-index: 1;
}
.ql-snow .ql-color-picker,
.ql-snow .ql-icon-picker {
  width: 28px;
}
.ql-snow .ql-color-picker .ql-picker-label,
.ql-snow .ql-icon-picker .ql-picker-label {
  padding: 2px 4px;
}
.ql-snow .ql-color-picker .ql-picker-label svg,
.ql-snow .ql-icon-picker .ql-picker-label svg {
  right: 4px;
}
.ql-snow .ql-icon-picker .ql-picker-options {
  padding: 4px 0px;
}
.ql-snow .ql-icon-picker .ql-picker-item {
  height: 24px;
  width: 24px;
  padding: 2px 4px;
}
.ql-snow .ql-color-picker .ql-picker-options {
  padding: 3px 5px;
  width: 152px;
}
.ql-snow .ql-color-picker .ql-picker-item {
  border: 1px solid transparent;
  float: left;
  height: 16px;
  margin: 2px;
  padding: 0px;
  width: 16px;
}
.ql-snow .ql-picker:not(.ql-color-picker):not(.ql-icon-picker) svg {
  position: absolute;
  margin-top: -9px;
  right: 0;
  top: 50%;
  width: 18px;
}
.ql-snow .ql-picker.ql-header .ql-picker-label[data-label]:not([data-label=''])::before,
.ql-snow .ql-picker.ql-font .ql-picker-label[data-label]:not([data-label=''])::before,
.ql-snow .ql-picker.ql-size .ql-picker-label[data-label]:not([data-label=''])::before,
.ql-snow .ql-picker.ql-header .ql-picker-item[data-label]:not([data-label=''])::before,
.ql-snow .ql-picker.ql-font .ql-picker-item[data-label]:not([data-label=''])::before,
.ql-snow .ql-picker.ql-size .ql-picker-item[data-label]:not([data-label=''])::before {
  content: attr(data-label);
}
.ql-snow .ql-picker.ql-header {
  width: 98px;
}
.ql-snow .ql-picker.ql-header .ql-picker-label::before,
.ql-snow .ql-picker.ql-header .ql-picker-item::before {
  content: 'Normal';
}
.ql-snow .ql-picker.ql-header .ql-picker-label[data-value='1']::before,
.ql-snow .ql-picker.ql-header .ql-picker-item[data-value='1']::before {
  content: 'Heading 1';
}
.ql-snow .ql-picker.ql-header .ql-picker-label[data-value='2']::before,
.ql-snow .ql-picker.ql-header .ql-picker-item[data-value='2']::before {
  content: 'Heading 2';
}
.ql-snow .ql-picker.ql-header .ql-picker-label[data-value='3']::before,
.ql-snow .ql-picker.ql-header .ql-picker-item[data-value='3']::before {
  content: 'Heading 3';
}
.ql-snow .ql-picker.ql-header .ql-picker-label[data-value='4']::before,
.ql-snow .ql-picker.ql-header .ql-picker-item[data-value='4']::before {
  content: 'Heading 4';
}
.ql-snow .ql-picker.ql-header .ql-picker-label[data-value='5']::before,
.ql-snow .ql-picker.ql-header .ql-picker-item[data-value='5']::before {
  content: 'Heading 5';
}
.ql-snow .ql-picker.ql-header .ql-picker-label[data-value='6']::before,
.ql-snow .ql-picker.ql-header .ql-picker-item[data-value='6']::before {
  content: 'Heading 6';
}
.ql-snow .ql-picker.ql-header .ql-picker-item[data-value='1']::before {
  font-size: 2em;
}
.ql-snow .ql-picker.ql-header .ql-picker-item[data-value='2']::before {
  font-size: 1.5em;
}
.ql-snow .ql-picker.ql-header .ql-picker-item[data-value='3']::before {
  font-size: 1.17em;
}
.ql-snow .ql-picker.ql-header .ql-picker-item[data-value='4']::before {
  font-size: 1em;
}
.ql-snow .ql-picker.ql-header .ql-picker-item[data-value='5']::before {
  font-size: 0.83em;
}
.ql-snow .ql-picker.ql-header .ql-picker-item[data-value='6']::before {
  font-size: 0.67em;
}
.ql-snow .ql-picker.ql-font {
  width: 108px;
}
.ql-snow .ql-picker.ql-font .ql-picker-label::before,
.ql-snow .ql-picker.ql-font .ql-picker-item::before {
  content: 'Sans Serif';
}
.ql-snow .ql-picker.ql-font .ql-picker-label[data-value='serif']::before,
.ql-snow .ql-picker.ql-font .ql-picker-item[data-value='serif']::before {
  content: 'Serif';
}
.ql-snow .ql-picker.ql-font .ql-picker-label[data-value='monospace']::before,
.ql-snow .ql-picker.ql-font .ql-picker-item[data-value='monospace']::before {
  content: 'Monospace';
}
.ql-snow .ql-picker.ql-font .ql-picker-item[data-value='serif']::before {
  font-family: Georgia, Times New Roman, serif;
}
.ql-snow .ql-picker.ql-font .ql-picker-item[data-value='monospace']::before {
  font-family: Monaco, Courier New, monospace;
}
.ql-snow .ql-picker.ql-size {
  width: 98px;
}
.ql-snow .ql-picker.ql-size .ql-picker-label::before,
.ql-snow .ql-picker.ql-size .ql-picker-item::before {
  content: 'Normal';
}
.ql-snow .ql-picker.ql-size .ql-picker-label[data-value='small']::before,
.ql-snow .ql-picker.ql-size .ql-picker-item[data-value='small']::before {
  content: 'Small';
}
.ql-snow .ql-picker.ql-size .ql-picker-label[data-value='large']::before,
.ql-snow .ql-picker.ql-size .ql-picker-item[data-value='large']::before {
  content: 'Large';
}
.ql-snow .ql-picker.ql-size .ql-picker-label[data-value='huge']::before,
.ql-snow .ql-picker.ql-size .ql-picker-item[data-value='huge']::before {
  content: 'Huge';
}
.ql-snow .ql-picker.ql-size .ql-picker-item[data-value='small']::before {
  font-size: 10px;
}
.ql-snow .ql-picker.ql-size .ql-picker-item[data-value='large']::before {
  font-size: 18px;
}
.ql-snow .ql-picker.ql-size .ql-picker-item[data-value='huge']::before {
  font-size: 32px;
}
.ql-snow .ql-color-picker.ql-background .ql-picker-item {
  background-color: #fff;
}
.ql-snow .ql-color-picker.ql-color .ql-picker-item {
  background-color: #000;
}
.ql-toolbar.ql-snow {
  border: 1px solid #ccc;
  box-sizing: border-box;
  font-family: 'Helvetica Neue', 'Helvetica', 'Arial', sans-serif;
  padding: 8px;
}
.ql-toolbar.ql-snow .ql-formats {
  margin-right: 15px;
}
.ql-toolbar.ql-snow .ql-picker-label {
  border: 1px solid transparent;
}
.ql-toolbar.ql-snow .ql-picker-options {
  border: 1px solid transparent;
  box-shadow: rgba(0, 0, 0, 0.2) 0 2px 8px;
}
.ql-toolbar.ql-snow .ql-picker.ql-expanded .ql-picker-label {
  border-color: #ccc;
}
.ql-toolbar.ql-snow .ql-picker.ql-expanded .ql-picker-options {
  border-color: #ccc;
}
.ql-toolbar.ql-snow .ql-color-picker .ql-picker-item.ql-selected,
.ql-toolbar.ql-snow .ql-color-picker .ql-picker-item:hover {
  border-color: #000;
}
.ql-toolbar.ql-snow + .ql-container.ql-snow {
  border-top: 0px;
}
.ql-snow .ql-tooltip {
  background-color: #fff;
  border: 1px solid #ccc;
  box-shadow: 0px 0px 5px #ddd;
  color: #444;
  padding: 5px 12px;
  white-space: nowrap;
}
.ql-snow .ql-tooltip::before {
  content: 'Visit URL:';
  line-height: 26px;
  margin-right: 8px;
}
.ql-snow .ql-tooltip input[type='text'] {
  display: none;
  border: 1px solid #ccc;
  font-size: 13px;
  height: 26px;
  margin: 0px;
  padding: 3px 5px;
  width: 170px;
}
.ql-snow .ql-tooltip a.ql-preview {
  display: inline-block;
  max-width: 200px;
  overflow-x: hidden;
  text-overflow: ellipsis;
  vertical-align: top;
}
.ql-snow .ql-tooltip a.ql-action::after {
  border-right: 1px solid #ccc;
  content: 'Edit';
  margin-left: 16px;
  padding-right: 8px;
}
.ql-snow .ql-tooltip a.ql-remove::before {
  content: 'Remove';
  margin-left: 8px;
}
.ql-snow .ql-tooltip a {
  line-height: 26px;
}
.ql-snow .ql-tooltip.ql-editing a.ql-preview,
.ql-snow .ql-tooltip.ql-editing a.ql-remove {
  display: none;
}
.ql-snow .ql-tooltip.ql-editing input[type='text'] {
  display: inline-block;
}
.ql-snow .ql-tooltip.ql-editing a.ql-action::after {
  border-right: 0px;
  content: 'Save';
  padding-right: 0px;
}
.ql-snow .ql-tooltip[data-mode='link']::before {
  content: 'Enter link:';
}
.ql-snow .ql-tooltip[data-mode='formula']::before {
  content: 'Enter formula:';
}
.ql-snow .ql-tooltip[data-mode='video']::before {
  content: 'Enter video:';
}
.ql-snow a {
  color: #06c;
}
</style>
